import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import FlowchartCanvas from './components/FlowchartCanvas';
import FlowchartOverlays from './components/FlowchartOverlays';
import HandlesLayer from './components/HandlesLayer';
import BulkActionsToolbar from '../components/BulkActionsToolbar';
import FlowchartEmptyStates from './components/FlowchartEmptyStates';
import FlowchartDropOverlay from './components/FlowchartDropOverlay';
import FlowchartMenus from './components/FlowchartMenus';
import { useFlowchartViewSetup } from './hooks/useFlowchartViewSetup';
import { useBridgeDeletePrompt } from './hooks/useBridgeDeletePrompt';
import { useConnectedNodePlacement } from './hooks/useConnectedNodePlacement';
import { useHideEdgesAwaitingElk } from './hooks/useHideEdgesAwaitingElk';
import { getContainerClasses } from './utils/containerClasses';
import { buildFlowchartHandlers } from './utils/buildFlowchartHandlers';
import {
  buildOverlaysProps,
  buildCanvasProps,
  buildHandlesLayerProps,
  buildBulkActionsProps,
} from './utils/propBuilders';
import { clearSelectedNodeClasses } from './hooks/utils/nodeSelectionDom';
import { clearFlowchartTextSelections } from './hooks/utils/clearFlowchartTextSelections';
import './styles/flowchart.css';
import './styles/edges.css';
import './styles/node/index.css';

/**
 * FlowchartView Component
 *
 * Displays tasks as an interactive flowchart with nodes and edges.
 * Orchestrates hooks and delegates rendering to FlowchartCanvas and FlowchartOverlays.
 */
const FlowchartView = forwardRef((props, ref) => {
  const {
    tasks = [],
    statuses,
    allCategories = [],
    allTags = [],
    nestedSprints = [],
    chains = [],
    autoArrange = false,
    campaignId = '',
    filters = {},
    showActionableOnly = false,
    initialFocusedTaskIds = null,
    isMultiCampaignMode = false,
    activeView = null,
    multiCampaignCampaigns = [],
    flowViewVisibility = {},
    hideViewExplainerModal = false,
    cardDisplaySettings = {},
    customFields = [],
    trackpadPanSensitivity,
    trackpadZoomSensitivity,
    currentView,
    customViews = [],
    activeCustomViewId = null,
    sprintName = '',
    sprintIcon = null,
    sprintProgress = 0,
    breadcrumbPath = [],
    localSprint = null,
    allSprints = [],
    loadFullSprint,
    onTidyToolbarBusyChange,
  } = props;

  const bridge = useBridgeDeletePrompt(tasks, props.onDeleteTask, props.onDeleteTasks);
  const handlers = buildFlowchartHandlers(props, bridge);

  const {
    core,
    isAnimatingReorder,
    isAnimatingFilter,
    handleChainMoveLeftWithSwap,
    handleChainMoveRightWithSwap,
    hoveredMissionId,
    handleMissionHover,
    handleMissionLeave,
  } = useFlowchartViewSetup({
    ref,
    tasks,
    statuses,
    nestedSprints,
    allTags,
    customFields,
    autoArrange,
    campaignId,
    isMultiCampaignMode,
    trackpadPanSensitivity,
    trackpadZoomSensitivity,
    onDependencyChange: handlers.onDependencyChange,
    onConditionsChange: handlers.onConditionsChange,
    onPositionChange: handlers.onPositionChange,
    onPositionChangeBatch: handlers.onPositionChangeBatch,
    wrapLastUndoAction: props.wrapLastUndoAction,
    onApplyTaskPositions: props.onApplyTaskPositions,
    onDeleteTasks: handlers.onDeleteTasks,
    onFocusModeChange: handlers.onFocusModeChange,
    onNestTasks: handlers.onNestTasks,
    onPasteTasks: handlers.onPasteTasks,
    onCreateConnectedTask: handlers.onCreateConnectedTask,
    onInsertNodeOnEdge: handlers.onInsertNodeOnEdge,
    onInsertMilestoneOnEdge: handlers.onInsertMilestoneOnEdge,
    onNestCampaign: handlers.onNestCampaign,
    onAddTask: handlers.onAddTask,
    initialFocusedTaskIds,
    chains,
    filters,
    showActionableOnly,
    onChainMoveLeft: handlers.onChainMoveLeft,
    onChainMoveRight: handlers.onChainMoveRight,
    onAddCampaignToView: handlers.onAddCampaignToView,
    multiCampaignCampaigns,
    openCampaignPickerForPaste: handlers.openCampaignPickerForPaste,
    localSprint,
    allSprints,
    loadFullSprint,
    activeView,
    currentView,
  });

  const onTidyToolbarBusyChangeRef = useRef(onTidyToolbarBusyChange);
  onTidyToolbarBusyChangeRef.current = onTidyToolbarBusyChange;

  useLayoutEffect(() => {
    onTidyToolbarBusyChange?.(core.isTidyToolbarBusy);
  }, [core.isTidyToolbarBusy, onTidyToolbarBusyChange]);

  useEffect(
    () => () => {
      onTidyToolbarBusyChangeRef.current?.(false);
    },
    [],
  );

  const hideEdgesAwaitingElk = useHideEdgesAwaitingElk({
    isMultiCampaignMode,
    measuredElkSettled: core.measuredElkSettled,
    taskCount: tasks.length,
  });

  const containerClasses = getContainerClasses({
    isPanning: core.isPanning,
    isDragging: core.isDragging,
    isConnecting: core.isConnecting,
    isSelectingBox: core.isSelectingBox,
    isFocusMode: core.isFocusMode,
    isAnimatingIntoFocus: core.isAnimatingIntoFocus,
    isAnimatingOutOfFocus: core.isAnimatingOutOfFocus,
    isAnimatingReorder,
    isAnimatingTidy: core.isAnimatingTidy,
    isAnimatingLayout: core.isAnimatingLayout,
    isAnimatingPan: core.isAnimatingPan,
    isAnimatingFilter,
    isViewReady: core.isViewReady,
    hideEdgesAwaitingElk,
  });

  const handleClearSelection = () => {
    clearFlowchartTextSelections();
    clearSelectedNodeClasses();
    const toolbar = document.querySelector('.bulk-actions-toolbar');
    if (toolbar) toolbar.style.display = 'none';
    core.setSelectedNodes(new Set());
  };

  const placement = useConnectedNodePlacement({
    tasks,
    handleCreateConnectedNode: core.handleCreateConnectedNode,
    pasteConnectedToHost: core.pasteConnectedToHost,
  });

  const coreWithPlacement = { ...core, handleCreateConnectedNode: placement.createConnectedNode };
  const propsContext = {
    props: {
      ...props,
      tasks,
      statuses,
      allCategories,
      allTags,
      nestedSprints,
      chains,
      sprintName,
      sprintIcon,
      sprintProgress,
      breadcrumbPath,
      isMultiCampaignMode,
      activeView,
      multiCampaignCampaigns,
      flowViewVisibility,
      hideViewExplainerModal,
      cardDisplaySettings,
      customFields,
      currentView,
      customViews,
      activeCustomViewId,
    },
    core: coreWithPlacement,
    handlers,
  };
  const overlaysProps = buildOverlaysProps({ ...propsContext, hoveredMissionId, handleMissionHover, handleMissionLeave, initialFocusedTaskIds });
  const canvasProps = buildCanvasProps({ ...propsContext, handleChainMoveLeftWithSwap, handleChainMoveRightWithSwap, hoveredMissionId, handleMissionHover, handleMissionLeave });
  const handlesLayerProps = buildHandlesLayerProps(propsContext);
  const bulkActionsProps = buildBulkActionsProps({ ...propsContext, handleClearSelection });

  return (
    <div
      ref={core.containerRef}
      className={containerClasses}
      onMouseDown={core.handleCanvasMouseDown}
      onMouseUp={core.handleMouseUp}
      onContextMenu={core.handleContextMenu}
      onClick={core.handleCanvasClick}
      onDragOver={core.campaignDrop.handleCampaignDragOver}
      onDragLeave={core.campaignDrop.handleCampaignDragLeave}
      onDrop={core.campaignDrop.handleCampaignDrop}
      tabIndex={0}
    >
      <div className="flowchart__animation-blocker" />

      <FlowchartOverlays {...overlaysProps} />
      <FlowchartCanvas {...canvasProps} />
      <HandlesLayer {...handlesLayerProps} />

      <FlowchartDropOverlay
        campaignDragPos={core.campaignDrop.campaignDragPos}
        isMultiCampaignDragOver={core.campaignDrop.isMultiCampaignDragOver}
        isMultiCampaignMode={isMultiCampaignMode}
      />

      <FlowchartEmptyStates
        taskCount={tasks.length}
        isFocusMode={core.isFocusMode}
        isAnimatingIntoFocus={core.isAnimatingIntoFocus}
        focusedTaskIds={core.focusedTaskIds}
        isMultiCampaignMode={isMultiCampaignMode}
        onShowAddMissionArrow={handlers.onShowAddMissionArrow}
      />

      <BulkActionsToolbar {...bulkActionsProps} />

      <FlowchartMenus
        canvasMenuRef={core.canvasMenuRef}
        onAddMission={core.handleAddMissionAtPosition}
        onAddDecision={core.handleAddDecisionAtPosition}
        onAddWait={core.handleAddWaitAtPosition}
        onCreateCampaign={props.onCreateCampaign}
        onPaste={core.handlePasteAtPosition}
        onCloseContextMenu={core.closeCanvasContextMenu}
        bridgePrompt={bridge.bridgePrompt}
        onBridge={() => bridge.resolveBridgePrompt(true)}
        onBreak={() => bridge.resolveBridgePrompt(false)}
        onCancelBridge={() => bridge.setBridgePrompt(null)}
        pendingMilestonePlacement={placement.pendingMilestonePlacement}
        onCancelMilestone={() => placement.setPendingMilestonePlacement(null)}
        onChooseMilestone={placement.confirmMilestonePlacement}
      />
    </div>
  );
});

FlowchartView.displayName = 'FlowchartView';

export default FlowchartView;
